import { canAddUserToRecord } from 'util/record';
import { escapeRegExp } from 'util/regex';
import { SUPPLIER_ID, SupplierVendorMappingCollection } from 'constants/supplier';
import { SupplierGroupType } from 'util/transform/supplierGroup';

//
// --- Functions ---

export function canAddSupplierToBidEvent(supplier: any, bidTeams: ReadonlyArray<any>) {
  // you can only add a supplier to a bid event if you can also add a user to the supplier
  // the supplier company must also exist in EIP
  return !isSupplierOnBidEvent(supplier, bidTeams) && canAddUserToRecord(supplier);
}

export function canAddSupplierToPortal(supplier: any, supplierGroups: ReadonlyArray<any>) {
  // you can only add a supplier to a portal if you can also add a user to the supplier
  // the supplier company must also exist in EIP
  return !isSupplierOnPortal(supplier, supplierGroups) && canAddUserToRecord(supplier);
}

//

function isSupplierOnProduct(supplier: any, supplierTeams: ReadonlyArray<any>) {
  // If this supplier represents an unfamilied vendor, need to use the EIP company key
  // Unfamilied vendors are stored by EIP key in bid-teams since they all share the same supplier id (S_0)
  const supplierId =
    supplier.parentId === SUPPLIER_ID.UNFAMILIED
      ? `${SUPPLIER_ID.EIP_COMPANY_PREFIX}${supplier.eipCompanyKey}`
      : supplier.id;

  // return true if supplier id not in any bid teams supplier list
  return supplierTeams.some(supplierTeam => supplierTeam.suppliers[supplierId]);
}

export function isSupplierOnBidEvent(supplier: any, bidTeams: ReadonlyArray<any>) {
  return isSupplierOnProduct(supplier, bidTeams);
}

export function isSupplierOnPortal(supplier: any, supplierGroups: ReadonlyArray<any>) {
  return isSupplierOnProduct(supplier, supplierGroups);
}

/**
 * Takes in a supplier id and return true if it is an id for an EIP company. Returns false otherwise.
 */

const eipSupplierIdRegex = new RegExp(`^${escapeRegExp(SUPPLIER_ID.EIP_COMPANY_PREFIX)}`, 'i');

export function isEIPSupplierId(supplierId: string): boolean {
  if (!supplierId || typeof supplierId !== 'string') {
    return false;
  }

  return eipSupplierIdRegex.test(supplierId);
}

const familiedSupplierRegex = new RegExp(`^${SUPPLIER_ID.FAMILIED_PREFIX}\\d+`, 'i');

export function isSupplierFamilied(supplierId: string): boolean {
  return familiedSupplierRegex.test(supplierId.toString());
}

/**
 * Build up list of supplier Ids to send to the supplier search service to check their diversity status.
 */
export function getAllSupplierIdsForFrequentContacts(frequentContacts: ReadonlyArray<any>) {
  return Array.isArray(frequentContacts) && frequentContacts.length > 0
    ? frequentContacts.reduce(
        (allSupplierIds, currentFrequentContact) =>
          allSupplierIds.concat(getAllSupplierIdsForSupplierTree(currentFrequentContact)),
        []
      )
    : [];
}

/**
 * Get all of the supplier ids inside one supplier family tree.
 */
export function getAllSupplierIdsForSupplierTree(supplier: any): ReadonlyArray<string> {
  if (!Array.isArray(supplier.children)) return [];
  return supplier.children.reduce(
    (supplierIds: ReadonlyArray<string>, currentChild: any) => {
      if (currentChild.isSupplier) {
        return supplierIds.concat(...getAllSupplierIdsForSupplierTree(currentChild));
      }
      return supplierIds;
    },
    [supplier.id]
  );
}

/**
 * A generic function for iterating a supplier team (ie. a bid team or a supplier group) and returning a list
 * of company keys for a given supplier id.
 *
 * Both bid teams and supplier groups store an array of company keys, but they are listed under different names.
 * A `companyKeyListPropertyName` must be passed in for the specific type of supplier team being searched.
 *
 * NOTE: Exported for unit testing only - This should be treated like an internal function, not used externally
 * use `getRelatedCompanyKeysForSupplierIdOnBidTeam` or `getRelatedCompanyKeysForSupplierIdOnSupplierGroup` externally
 */
export function getRelatedCompanyKeysForSupplierId(
  supplierId: string,
  supplierTeam: any,
  companyKeyListPropertyName: string,
  supplierVendorMappings: SupplierVendorMappingCollection
) {
  if (
    !supplierId ||
    typeof supplierId !== 'string' ||
    !companyKeyListPropertyName ||
    typeof companyKeyListPropertyName !== 'string'
  ) {
    return [];
  }

  if (supplierId.includes(SUPPLIER_ID.EIP_COMPANY_PREFIX)) {
    return [supplierId.replace(SUPPLIER_ID.EIP_COMPANY_PREFIX, '')];
  }

  if (
    !supplierTeam ||
    !supplierTeam[companyKeyListPropertyName] ||
    !Array.isArray(supplierTeam[companyKeyListPropertyName]) ||
    !Array.isArray(supplierVendorMappings)
  ) {
    return [];
  }

  const memberCompanyKeyList = [];

  for (const supplierVendorKey in supplierVendorMappings) {
    const supplierVendorObject = supplierVendorMappings[supplierVendorKey];
    if (supplierVendorObject.supplier.id === supplierId) {
      const vendorsList = supplierVendorObject.vendors;
      for (const vendorListKey in vendorsList) {
        const vendorEIPKey = vendorsList[vendorListKey].eip_companykey;
        if (supplierTeam[companyKeyListPropertyName].includes(vendorEIPKey)) {
          memberCompanyKeyList.push(vendorEIPKey.toString());
        }
      }
    }
  }

  return memberCompanyKeyList;
}

/**
 * generate a list of company keys related to a given supplier id on a bid team
 */
export function getRelatedCompanyKeysForSupplierIdOnBidTeam(
  supplierId: string,
  bidTeam: any,
  supplierVendorMappings: SupplierVendorMappingCollection
) {
  return getRelatedCompanyKeysForSupplierId(supplierId, bidTeam, 'bidTeamMemberKeys', supplierVendorMappings);
}

/**
 * generate a list of company keys related to a given supplier id on a supplier group
 */
export function getRelatedCompanyKeysForSupplierIdOnSupplierGroup(
  supplierId: string,
  supplierGroup: SupplierGroupType,
  supplierVendorMappings: SupplierVendorMappingCollection
) {
  return getRelatedCompanyKeysForSupplierId(
    supplierId,
    supplierGroup,
    'supplierPortalSupplierCompanyKeys',
    supplierVendorMappings
  );
}

/**
 * Determines if a tree node is a node for the first person in a company that also
 * has a sibling company. Takes in an optional exclude list, to exclude certain supplier
 * ids.
 *
 * This was initially created to determine if we should display the "Show Contacts" button
 * in search results and the exclude list if for nodes that a user has selected to view.
 */
export function isFirstPersonTreeNodeOfSupplierWithSiblings(
  treeNodeData: any,
  excludedSupplierIds: ReadonlyArray<string> = []
): boolean {
  if (!treeNodeData) {
    return false;
  }

  const excludedIds = Array.isArray(excludedSupplierIds) ? excludedSupplierIds : [];

  return (
    !!treeNodeData.hasSupplierSibling &&
    treeNodeData.supplierPersonIndex === 0 &&
    !excludedIds.includes(treeNodeData.supplierId)
  );
}

/**
 * return a supplier team (generic term for bid teams, supplier groups, etc) by id
 */
export function findSupplierTeamByTeamId(supplierTeams: ReadonlyArray<any>, id?: number): any | undefined {
  if (!Array.isArray(supplierTeams) || !supplierTeams.length || !id) {
    return undefined;
  }

  return supplierTeams.find(supplierTeam => supplierTeam.id === id);
}
